import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';

const AvailabilityMonitor = ({ db, usersList }) => {
    const [monitorAvailability, setMonitorAvailability] = useState([]); 
    const [loading, setLoading] = useState(false);

    const fetchMonitorData = async () => {
        setLoading(true);
        const dates = [];
        let d = new Date();
        // Move to previous Saturday if today isn't Sat
        while (d.getDay() !== 6) { d.setDate(d.getDate() - 1); }
        // Get last 5 weeks
        for (let i = 0; i < 5; i++) {
            dates.push(new Date(d)); // Saturday
            
            // Also grab Sunday to ensure we fetch the correct month if it crosses over
            const sun = new Date(d);
            sun.setDate(d.getDate() + 1);
            dates.push(sun);

            d.setDate(d.getDate() - 7);
        }
  
        const queriesToRun = [];
        const keys = new Set();
        dates.forEach(date => {
            const k = `${date.getFullYear()}-${date.getMonth()}`;
            if (!keys.has(k)) { keys.add(k); queriesToRun.push({ y: date.getFullYear(), m: date.getMonth() }); }
        });
  
        const results = [];
        for (const qParams of queriesToRun) {
            const q = query(collection(db, "availability"), where("year", "==", qParams.y), where("month", "==", qParams.m));
            const s = await getDocs(q);
            s.forEach(doc => results.push(doc.data()));
        }
        setMonitorAvailability(results);
        setLoading(false);
    };

    useEffect(() => {
        fetchMonitorData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Toggle Holiday Status (Sets Saturday to Holiday for simplicity in toggling)
    const toggleHoliday = async (initials, dateObj, currentStatus) => {
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth();
        const day = dateObj.getDate();
        const docId = `${initials}_${year}_${month}_${day}`;
        
        // If currently Holiday, revert to Unavailable (reset). If anything else, make Holiday.
        const newStatus = currentStatus === 'Holiday' ? 'Unavailable' : 'Holiday';

        // Optimistic UI update
        await setDoc(doc(db, "availability", docId), {
            initials,
            year,
            month,
            day,
            status: newStatus,
            updatedAt: new Date()
        }, { merge: true });

        fetchMonitorData();
    };

    // Filter drivers
    const targetDrivers = usersList.filter(u => u.jobRole === 'Full Time Driver' || u.jobRole === 'Part Time Driver');
    
    // Recalculate dates for render
    const last5Saturdays = [];
    let d = new Date();
    while(d.getDay() !== 6) { d.setDate(d.getDate() - 1); }
    for(let i=0; i<5; i++) {
        last5Saturdays.push(new Date(d));
        d.setDate(d.getDate() - 7);
    }
    // Reverse so the oldest date is on the left, and the most recent (current) is on the right
    last5Saturdays.reverse();

    return (
        <div className="card" style={{maxWidth:'900px', margin:'0 auto'}}>
            <div className="flex-between" style={{marginBottom:'20px'}}>
                <div>
                    <h3>Availability Monitor</h3>
                    <p className="text-sm">Click a box to toggle <span style={{backgroundColor:'#facc15', padding:'2px 6px', borderRadius:'4px', color:'black', fontWeight:'bold'}}>Holiday</span> status.</p>
                </div>
                <button onClick={fetchMonitorData} disabled={loading} style={{background:'var(--light)', color:'var(--text-main)', padding:'8px 12px', fontSize:'12px'}}>
                    {loading ? 'Refreshing...' : '🔄 Refresh'}
                </button>
            </div>
            
            <div style={{overflowX:'auto'}}>
                <table style={{textAlign:'left', width:'100%', borderCollapse:'collapse'}}>
                    <thead>
                        <tr style={{borderBottom:'2px solid var(--border)'}}>
                            <th style={{padding:'10px'}}>Driver</th>
                            <th style={{padding:'10px'}}>Role</th>
                            <th style={{padding:'10px', textAlign:'center'}}>
                                <div style={{marginBottom:'5px', fontSize:'11px', textTransform:'uppercase', color:'var(--text-muted)'}}>Last 5 Weeks</div>
                                <div style={{display:'flex', gap:'5px', justifyContent:'center'}}>
                                    {last5Saturdays.map((date, i) => (
                                        <div key={i} style={{width:'25px', textAlign:'center', fontSize:'9px', fontWeight:'bold', color:'var(--text-muted)'}}>
                                            {date.getDate()}/{date.getMonth() + 1}
                                        </div>
                                    ))}
                                </div>
                            </th>
                            <th style={{padding:'10px', textAlign:'center'}}>Score</th>
                            <th style={{padding:'10px', textAlign:'center'}}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {targetDrivers.map(u => {
                            let availableCount = 0;
                            let holidayCount = 0;
                            let notLegalCount = 0;
                            
                            const weekBoxes = last5Saturdays.map((satDate, index) => {
                                // Saturday
                                const satY = satDate.getFullYear();
                                const satM = satDate.getMonth();
                                const satD = satDate.getDate();
                                
                                // Sunday
                                const sunDate = new Date(satDate);
                                sunDate.setDate(satDate.getDate() + 1);
                                const sunY = sunDate.getFullYear();
                                const sunM = sunDate.getMonth();
                                const sunD = sunDate.getDate();

                                const recSat = monitorAvailability.find(rec => 
                                    rec.initials === u.initials && rec.year === satY && rec.month === satM && rec.day === satD
                                );
                                const recSun = monitorAvailability.find(rec => 
                                    rec.initials === u.initials && rec.year === sunY && rec.month === sunM && rec.day === sunD
                                );

                                // --- COMPOSITE LOGIC ---
                                let boxColor = '#94a3b8'; // Default Grey
                                let title = `Unavailable (${satD}/${satM+1} & ${sunD}/${sunM+1})`;
                                let type = 'Unavailable';

                                // Helper checks
                                const isWorking = (recSat?.managerDecision === 'Working') || (recSun?.managerDecision === 'Working');
                                const isAvailable = (recSat?.status === 'Available' || recSat?.status === 'Want to Work') || 
                                                    (recSun?.status === 'Available' || recSun?.status === 'Want to Work');
                                const isHoliday = (recSat?.status === 'Holiday') || (recSun?.status === 'Holiday');
                                const isNotLegal = (recSat?.managerDecision === 'Not Legal') || (recSun?.managerDecision === 'Not Legal');

                                // Priority 1: Working (Dark Green)
                                if (isWorking) {
                                    boxColor = '#15803d';
                                    title = "Working";
                                    type = 'Available';
                                }
                                // Priority 2: Available (Light Green)
                                else if (isAvailable) {
                                    boxColor = '#4ade80';
                                    title = "Available";
                                    type = 'Available';
                                }
                                // Priority 3: Holiday (Yellow)
                                else if (isHoliday) {
                                    boxColor = '#facc15';
                                    title = "Holiday";
                                    type = 'Holiday';
                                }
                                // Priority 4: Not Legal (Red)
                                else if (isNotLegal) {
                                    boxColor = '#dc2626';
                                    title = "Not Legal";
                                    type = 'NotLegal';
                                }

                                // Increment Counters
                                if (type === 'Available') availableCount++;
                                if (type === 'Holiday') holidayCount++;
                                if (type === 'NotLegal') notLegalCount++;

                                return (
                                    <div 
                                        key={index}
                                        // Toggle Saturday's status when clicked (Simplest way to set the flag)
                                        onClick={() => toggleHoliday(u.initials, satDate, recSat?.status)}
                                        title={title}
                                        style={{
                                            width:'25px', 
                                            height:'25px', 
                                            backgroundColor: boxColor, 
                                            borderRadius:'4px',
                                            cursor:'pointer',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '10px',
                                            color: type === 'Holiday' ? 'black' : 'white',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {satD}
                                    </div>
                                );
                            });

                            // Calculate Score
                            const measurableWeeks = 5 - holidayCount - notLegalCount;
                            const finalDenominator = measurableWeeks < 1 ? 1 : measurableWeeks; // Prevent divide by zero
                            
                            const ratio = availableCount / finalDenominator;
                            const threshold = u.jobRole === 'Full Time Driver' ? 0.33 : 0.25; 
                            
                            const isPassing = measurableWeeks === 0 ? true : ratio >= threshold;

                            return (
                                <tr key={u.initials} style={{borderBottom:'1px solid var(--light)'}}>
                                    <td style={{fontWeight:'500', padding:'10px'}}>{u.displayName}</td>
                                    <td className="text-sm" style={{padding:'10px'}}>{u.jobRole}</td>
                                    <td style={{padding:'10px'}}>
                                        <div style={{display:'flex', gap:'5px', justifyContent:'center'}}>
                                            {weekBoxes}
                                        </div>
                                    </td>
                                    <td style={{fontWeight:'bold', padding:'10px', textAlign:'center'}}>
                                        {measurableWeeks === 0 ? '-' : `${availableCount}/${measurableWeeks}`}
                                    </td>
                                    <td style={{padding:'10px', textAlign:'center'}}>
                                        {isPassing ? (
                                            <span style={{color:'#4ade80', fontWeight:'bold', background:'rgba(27,143,90,0.18)', padding:'2px 8px', borderRadius:'10px', fontSize:'11px'}}>PASS</span>
                                        ) : (
                                            <span style={{color:'var(--danger)', fontWeight:'bold', background:'rgba(179,38,30,0.15)', padding:'2px 8px', borderRadius:'10px', fontSize:'11px'}}>FAIL</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AvailabilityMonitor;